import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "../../../components/common/Badge";
import ActionButton from "../../../components/common/ActionButton";
import CashingUpReportPage from "./CashingUpReportPage";
import { useGetCashingUpPaidOutsReportQuery, useGetUsersQuery } from "../../../store/api";
import { useMemo } from "react";
import {
  formatAmountWithImpact,
  formatSignedCurrency,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";

const columns = [
  { key: "movementType", label: "TYPE" },
  { key: "amount", label: "AMOUNT" },
  { key: "reasonCode", label: "REASON" },
  { key: "status", label: "STATUS" },
  { key: "createdByName", label: "CREATED BY" },
  { key: "actions", label: "ACTIONS" },
];

const PaidOutsReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: users } = useGetUsersQuery();

  const userNameById = useMemo(() => {
    const map = new Map();
    (Array.isArray(users) ? users : []).forEach((user) => {
      const id = user.userId || user.id;
      const name = user.displayName || user.name || user.fullName || user.email || "";
      if (id && name) {
        map.set(id, name);
      }
    });
    return map;
  }, [users]);

  return (
    <CashingUpReportPage
      title="Paid Outs Report"
      subtitle="Review paid-out and other cash movement entries mirrored into Central."
      useQueryHook={useGetCashingUpPaidOutsReportQuery}
      collectionKeys={["paidOuts", "movements", "items", "rows", "data"]}
      columns={columns}
      showBusinessDate={false}
      showDateRange
      showLimit={false}
      showExport={false}
      buildRows={(collection) =>
        collection.map((movement) => ({
          id: movement.cashMovementId || `${movement.cashSessionId}-${movement.reasonCode}`,
          cashMovementId: movement.cashMovementId || "-",
          cashSessionId: movement.cashSessionId || "-",
          movementType: movement.movementType || "-",
          amountValue: Number(movement.amount || 0),
          amount: (() => {
            const value = Number(movement.amount || 0);
            const formatting = formatAmountWithImpact(
              ["PAID_OUT", "CASH_DROP", "TIP_PAYOUT"].includes(
                String(movement.movementType || "").toUpperCase(),
              )
                ? -Math.abs(value)
                : value,
            );
            return (
              <span
                className={
                  formatting.tone === "rose"
                    ? "font-semibold text-rose-700"
                    : formatting.tone === "emerald"
                      ? "font-semibold text-emerald-700"
                      : "font-semibold text-slate-700"
                }
              >
                {formatting.value}
              </span>
            );
          })(),
          reasonCode: movement.reasonCode || "-",
          statusValue: movement.status || "",
          status: (
            <Badge tone={getStatusTone(movement.status)}>
              {formatStatusLabel(movement.status)}
            </Badge>
          ),
          createdByName:
            movement.createdByUserName ||
            movement.createdByName ||
            movement.createdByDisplayName ||
            userNameById.get(movement.createdByUserId) ||
            userNameById.get(movement.createdBy) ||
            "-",
          actions: (
            <ActionButton
              label="View movement"
              icon="view"
              onClick={() =>
                navigate(
                  {
                    pathname: `/reporting/cashing-up/paid-outs/${movement.cashMovementId}`,
                    search: location.search,
                  },
                  { state: { movement } },
                )
              }
            />
          ),
        }))
      }
      buildStats={(_, rows) => {
        const totalRows = rows.length;
        const totalOut = rows.reduce((sum, row) => {
          return sum + Math.abs(Number(row.amountValue || 0));
        }, 0);
        const pendingRows = rows.filter((row) =>
          String(row.statusValue || "").toUpperCase().includes("PENDING"),
        ).length;
        const approvedRows = rows.filter((row) =>
          String(row.statusValue || "").toUpperCase().includes("APPROVED"),
        ).length;

        return [
          {
            label: "Rows",
            value: String(totalRows),
            helper: "Movement records returned by Central.",
            accent: "indigo",
          },
          {
            label: "Approved",
            value: String(approvedRows),
            helper: "Manager-reviewed movements.",
            accent: "emerald",
          },
          {
            label: "Pending",
            value: String(pendingRows),
            helper: "Awaiting review or sync confirmation.",
            accent: "amber",
          },
          {
            label: "Total Out",
            value: formatSignedCurrency(-Math.abs(totalOut)),
            helper: "Absolute amount of paid-out impact.",
            accent: "rose",
          },
        ];
      }}
      emptyText="No paid-out records found for the selected store and date range."
    />
  );
};

export default PaidOutsReport;
